import { TemplateColors } from '../types';

// Función para convertir un color HEX a componentes RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  // Eliminar el símbolo # si existe
  hex = hex.replace(/^#/, '');
  
  // Convertir formato abreviado (por ejemplo, #03F) a formato completo (por ejemplo, #0033FF)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Parsear componentes
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

// Función para convertir RGB a formato hexadecimal
export const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b]
    .map(x => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');
};

// Función para ajustar el brillo de un color (factor > 1 aclara, factor < 1 oscurece)
export const adjustBrightness = (hex: string, factor: number): string => {
  const { r, g, b } = hexToRgb(hex);
  
  return rgbToHex(
    r * factor,
    g * factor,
    b * factor
  );
};

// Función para generar colores derivados para templates
export const generateTemplateColors = (baseColor: string): TemplateColors => {
  // Asegurarnos de que el baseColor siempre tenga formato correcto
  if (!baseColor || typeof baseColor !== 'string') {
    console.warn('Color base inválido, usando color predeterminado #40E0D0');
    baseColor = '#40E0D0'; // Color turquesa por defecto
  }
  
  // Asegurarnos de que tenga el formato correcto con #
  if (!baseColor.startsWith('#')) {
    baseColor = '#' + baseColor;
  }
  
  // Generar un color más oscuro para el gradiente
  const darkColor = adjustBrightness(baseColor, 0.7);
  
  // Convertir el color a RGB para comprobar si es claro u oscuro
  const { r, g, b } = hexToRgb(baseColor);
  
  // Fórmula para determinar si un color es claro u oscuro (valor de luminosidad)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Si el color es muy claro, usar un color más oscuro para el texto para asegurar legibilidad
  let textColor = baseColor;
  if (luminance > 0.7) {
    textColor = adjustBrightness(baseColor, 0.7); // Oscurecer para texto
  }
  
  console.log("Generando colores desde:", baseColor);
  console.log("Color oscuro generado:", darkColor);
  console.log("Color de texto generado:", textColor);
  
  return {
    // Evitamos clases dinámicas de Tailwind que causan problemas en build
    sidebar: '', // Usar sidebarFrom y sidebarTo con style en su lugar
    accent: '', // Usar accentColor con style en su lugar
    accentBg: '', // Usar accentColor con style en su lugar
    
    // Valores directos para ser usados con style
    sidebarFrom: baseColor,
    sidebarTo: darkColor,
    accentColor: textColor
  };
};

// Paleta de colores predefinidos para mostrar como opciones
export const COLOR_PALETTE = [
  '#4B6A9B', // Azul original
  '#2D6E5A', // Verde oscuro
  '#B8860B', // Dorado
  '#DB7093', // Rosa medio
  '#8FBC8F', // Verde salvia
  '#4B0082', // Índigo
  '#40E0D0', // Turquesa
  '#696969', // Gris oscuro
  '#800000', // Borgoña
  '#FF6347', // Tomate
  '#8A2BE2', // Azul violeta
  '#008080'  // Verde azulado
]; 